import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import './styles/theme.css'
import './styles/mobile.css'
import AdminLayout from './components/layout/AdminLayout'
import LoginPage from './pages/LoginPage'
import ForgotPasswordPage from './pages/ForgotPasswordPage'
import ResetPasswordPage from './pages/ResetPasswordPage'
import DashboardPage from './pages/DashboardPage'
import AnalyticsPage from './pages/AnalyticsPage'
import OrdersPage from './pages/OrdersPage'
import LiveMapPage from './pages/LiveMapPage'
import ManagementPage from './pages/ManagementPage'
import SettingsPage from './pages/SettingsPage'
import ActivityLogPage from './pages/ActivityLogPage'
import MarketingPage from './pages/MarketingPage'
import ComplaintsPage from './pages/ComplaintsPage'
import CashPage from './pages/CashPage'
import ExpensesPage from './pages/ExpensesPage'
import BroadcastPage from './pages/BroadcastPage'
import ExpressOrdersPage from './pages/ExpressOrdersPage'
import KycApprovalPage from './pages/KycApprovalPage'
import ServicePricingPage from './pages/ServicePricingPage'
import CustomersPage from './pages/CustomersPage'
import ServiceCentersPage from './pages/ServiceCentersPage'
import DeliveryZonesPage from './pages/DeliveryZonesPage'
import DisputesPage from './pages/DisputesPage'
import WalletPage from './pages/wallet/WalletPage'
import B2bClientsPage from './pages/b2b/B2bClientsPage'
import B2bClientDetailPage from './pages/b2b/B2bClientDetailPage'
import B2bBillingConsolePage from './pages/b2b/B2bBillingConsolePage'
import B2bGenerateInvoicePage from './pages/b2b/B2bGenerateInvoicePage'
import B2bInvoicesPage from './pages/b2b/B2bInvoicesPage'
import B2bInvoicePreviewPage from './pages/b2b/B2bInvoicePreviewPage'
import SlaMonitorPage from './pages/SlaMonitorPage'
import NotificationCenterPage from './pages/notifications/NotificationCenterPage'
import CampaignComposerPage from './pages/notifications/CampaignComposerPage'
import CampaignDetailPage from './pages/notifications/CampaignDetailPage'
import TemplateLibraryPage from './pages/notifications/TemplateLibraryPage'
import NotificationSettingsPage from './pages/notifications/NotificationSettingsPage'
import ForbiddenPage from './pages/ForbiddenPage'
import ServiceUnavailablePage from './pages/ServiceUnavailablePage'
import { useServiceStatus } from './hooks/useServiceStatus'
import RequirePermission from './components/auth/RequirePermission'
import RequireFeature from './components/auth/RequireFeature'
import type { Permission } from './lib/permissions'
import type { ReactNode } from 'react'

const guard = (permission: Permission, element: ReactNode) => (
  <RequirePermission permission={permission}>{element}</RequirePermission>
)

/**
 * Availability gate.
 *
 * Wraps the entire router rather than individual routes, so there is one place
 * the portal closes and no route — existing or added later — can be reachable
 * while the admin API is returning 503. Login is covered too: leaving a sign-in
 * form usable in front of an unreachable backend walks the operator into a dead
 * end, since the login request would fail anyway.
 *
 * The state is owned by the server; this component only reads it. Nothing
 * renders until the probe answers, so the dashboard never flashes before being
 * replaced.
 */
function ServiceGate({ children }: { children: ReactNode }) {
  const { unavailable, ready } = useServiceStatus()

  if (!ready) {
    return null
  }

  return unavailable ? <ServiceUnavailablePage /> : <>{children}</>
}

export default function App() {
  return (
    <ServiceGate>
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/" element={<AdminLayout />}>
          {/* Dashboard is the safe landing page for every authenticated role. */}
          <Route index element={<DashboardPage />} />
          <Route path="analytics" element={guard('analytics.view', <AnalyticsPage />)} />
          <Route path="orders" element={<RequireFeature flag="adminOrders">{guard('orders.view', <OrdersPage />)}</RequireFeature>} />
          <Route path="express-orders" element={guard('orders.view', <ExpressOrdersPage />)} />
          <Route path="live-map" element={guard('orders.view', <LiveMapPage />)} />
          <Route path="complaints" element={guard('complaints.view', <ComplaintsPage />)} />
          <Route path="cash" element={guard('cash.view', <CashPage />)} />
          <Route path="expenses" element={guard('expenses.manage', <ExpensesPage />)} />
          <Route path="broadcast" element={<RequireFeature flag="whatsappBlast">{guard('broadcast.send', <BroadcastPage />)}</RequireFeature>} />
          <Route path="kyc-approval" element={guard('agents.view', <KycApprovalPage />)} />
          <Route path="service-pricing" element={guard('services.update', <ServicePricingPage />)} />
          <Route path="customers" element={guard('customers.view', <CustomersPage />)} />
          {/* B2B bulk billing. Reading is b2b.view; entering a room's laundry is
              b2b.bill (an operator's daily job); generating an invoice closes a
              cycle irreversibly, so it is b2b.manage. */}
          <Route path="wallet" element={guard('wallet.view', <WalletPage />)} />
          <Route path="b2b/clients" element={guard('b2b.view', <B2bClientsPage />)} />
          <Route path="b2b/clients/:clientId" element={guard('b2b.view', <B2bClientDetailPage />)} />
          <Route path="b2b/billing" element={guard('b2b.bill', <B2bBillingConsolePage />)} />
          <Route path="b2b/invoices" element={guard('b2b.view', <B2bInvoicesPage />)} />
          <Route path="b2b/invoices/new" element={guard('b2b.manage', <B2bGenerateInvoicePage />)} />
          <Route path="b2b/invoices/:invoiceId" element={guard('b2b.view', <B2bInvoicePreviewPage />)} />
          <Route path="service-centers" element={guard('settings.update', <ServiceCentersPage />)} />
          <Route path="delivery-zones" element={guard('settings.update', <DeliveryZonesPage />)} />
          <Route path="disputes" element={guard('complaints.view', <DisputesPage />)} />
          <Route path="sla-monitor" element={guard('orders.view', <SlaMonitorPage />)} />
          <Route path="management" element={guard('agents.view', <ManagementPage />)} />
          <Route path="settings" element={guard('settings.update', <SettingsPage />)} />
          <Route path="activity-log" element={guard('analytics.view', <ActivityLogPage />)} />
          <Route path="marketing" element={guard('marketing.view', <MarketingPage />)} />
          {/* Notification Center. 'new' and 'templates' are declared before the
              ':id' route so they are never parsed as a campaign id. */}
          <Route path="notifications" element={guard('notifications.view', <NotificationCenterPage />)} />
          <Route path="notifications/new" element={guard('notifications.create', <CampaignComposerPage />)} />
          <Route path="notifications/templates" element={guard('notifications.view', <TemplateLibraryPage />)} />
          <Route path="notifications/settings" element={guard('settings.update', <NotificationSettingsPage />)} />
          <Route path="notifications/:id" element={guard('notifications.view', <CampaignDetailPage />)} />
          <Route path="notifications/:id/edit" element={guard('notifications.create', <CampaignComposerPage />)} />
          <Route path="403" element={<ForbiddenPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </BrowserRouter>
    </ServiceGate>
  )
}
